import React from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import PipelinePage from './pages/PipelinePage';
import PipelineBuilderPage from './pages/PipelineBuilderPage';
import LeadsPage from './pages/LeadsPage';
import AutomationsPage from './pages/AutomationsPage';
import AdminPage from './pages/AdminPage';
import { getUpworkStatus, getUpworkAuthUrl, disconnectUpwork, getUpworkRoomsStatus, syncUpworkRooms, getUpworkProposalsStatus, syncUpworkProposals } from './api/upworkApi';

function UpworkButton() {
  const [connected, setConnected] = React.useState(null); // null=loading
  const [showMenu, setShowMenu] = React.useState(false);
  const [roomsStatus, setRoomsStatus] = React.useState(null); // null=unknown, else { bootstrapComplete, roomCount, messageCount }
  const [syncingRooms, setSyncingRooms] = React.useState(false);
  const [proposalsStatus, setProposalsStatus] = React.useState(null); // null=unknown, else { bootstrapComplete, proposalCount }
  const [syncingProposals, setSyncingProposals] = React.useState(false);

  const checkStatus = React.useCallback(() => {
    getUpworkStatus()
      .then(r => setConnected(r.data.connected))
      .catch(() => setConnected(false));
  }, []);

  const checkRoomsStatus = React.useCallback(() => {
    getUpworkRoomsStatus()
      .then(r => setRoomsStatus(r.data))
      .catch(() => {});
  }, []);

  const checkProposalsStatus = React.useCallback(() => {
    getUpworkProposalsStatus()
      .then(r => setProposalsStatus(r.data))
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    // Handle redirect back from Upwork OAuth
    const params = new URLSearchParams(window.location.search);
    const result = params.get('upwork');
    if (result) {
      window.history.replaceState({}, '', window.location.pathname);
      if (result === 'connected') setConnected(true);
      if (result === 'error') alert('Upwork authorization failed. Please try again.');
    }
    checkStatus();
  }, [checkStatus]);

  React.useEffect(() => {
    if (connected) { checkRoomsStatus(); checkProposalsStatus(); }
  }, [connected, checkRoomsStatus, checkProposalsStatus]);

  const toggleMenu = () => {
    setShowMenu(m => {
      const next = !m;
      if (next) { checkRoomsStatus(); checkProposalsStatus(); } // pick up progress made by the background hourly job
      return next;
    });
  };

  const connect = async () => {
    try {
      const r = await getUpworkAuthUrl();
      window.location.href = r.data.url;
    } catch {
      alert('Could not get Upwork authorization URL.');
    }
  };

  const handleSyncRooms = async () => {
    setSyncingRooms(true);
    setShowMenu(false);
    try {
      const r = await syncUpworkRooms();
      const {
        mode = 'incremental',
        roomsScanned = 0,
        roomsCreated = 0,
        roomsUpdated = 0,
        messagesStored = 0,
        leadsCreated = 0,
        requestsUsed = 0,
        rateLimitHit = false,
        errors = [],
      } = r.data;
      let msg = `Conversation sync complete (${mode}):\n• ${roomsScanned} room${roomsScanned !== 1 ? 's' : ''} scanned\n• ${roomsCreated} created, ${roomsUpdated} updated\n• ${messagesStored} new message${messagesStored !== 1 ? 's' : ''} stored\n• ${leadsCreated} new lead${leadsCreated !== 1 ? 's' : ''} created`;
      msg += `\nAPI requests: ${requestsUsed}`;
      if (rateLimitHit) msg += `\n⚠️ Rate limit reached — will resume next sync`;
      if (errors.length > 0) msg += `\n\nWarnings:\n• ${errors.join('\n• ')}`;
      alert(msg);
    } catch {
      alert('Conversation sync failed. Check that Upwork is still connected.');
    } finally {
      setSyncingRooms(false);
      checkRoomsStatus();
    }
  };

  const handleSyncProposals = async () => {
    setSyncingProposals(true);
    setShowMenu(false);
    try {
      const r = await syncUpworkProposals();
      const {
        mode = 'incremental',
        proposalsScanned = 0,
        leadsCreated = 0,
        leadsUpdated = 0,
        requestsUsed = 0,
        rateLimitHit = false,
        errors = [],
      } = r.data;
      let msg = `Proposal sync complete (${mode}):\n• ${proposalsScanned} proposal${proposalsScanned !== 1 ? 's' : ''} scanned\n• ${leadsCreated} new lead${leadsCreated !== 1 ? 's' : ''} created, ${leadsUpdated} updated`;
      msg += `\nAPI requests: ${requestsUsed}`;
      if (rateLimitHit) msg += `\n⚠️ Rate limit reached — will resume next sync`;
      if (errors.length > 0) msg += `\n\nWarnings:\n• ${errors.join('\n• ')}`;
      alert(msg);
    } catch {
      alert('Proposal sync failed. Check that Upwork is still connected.');
    } finally {
      setSyncingProposals(false);
      checkProposalsStatus();
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Upwork account? Syncing will stop until you reconnect.')) return;
    setShowMenu(false);
    await disconnectUpwork();
    setConnected(false);
  };

  if (connected === null) return null;

  if (!connected) {
    return (
      <button style={s.upworkBtn} onClick={connect}>
        Connect Upwork
      </button>
    );
  }

  const bootstrapping = !roomsStatus || !roomsStatus.bootstrapComplete;
  const roomsSyncDisabled = bootstrapping || syncingRooms;
  const roomsSyncLabel = syncingRooms ? 'Syncing conversations...' : bootstrapping ? 'Conversations syncing...' : 'Sync conversations';
  const roomsSyncTitle = bootstrapping
    ? 'The initial conversation backfill is still running in the background — this becomes available once it finishes'
    : undefined;

  const proposalsBootstrapping = !proposalsStatus || !proposalsStatus.bootstrapComplete;
  const proposalsSyncDisabled = proposalsBootstrapping || syncingProposals;
  const proposalsSyncLabel = syncingProposals ? 'Syncing proposals...' : proposalsBootstrapping ? 'Proposals syncing...' : 'Sync proposals';
  const proposalsSyncTitle = proposalsBootstrapping
    ? 'The initial proposal backfill is still running in the background — this becomes available once it finishes'
    : undefined;

  return (
    <div style={{ position: 'relative' }}>
      <button style={s.upworkConnectedBtn} onClick={toggleMenu}>
        <span style={s.upworkDot} />
        Upwork
      </button>
      {showMenu && (
        <div style={s.upworkMenu} onMouseLeave={() => setShowMenu(false)}>
          <button
            style={{ ...s.upworkMenuItem, ...(roomsSyncDisabled ? s.upworkMenuItemDisabled : {}) }}
            onClick={handleSyncRooms}
            disabled={roomsSyncDisabled}
            title={roomsSyncTitle}
          >
            {roomsSyncLabel}
          </button>
          <button
            style={{ ...s.upworkMenuItem, ...(proposalsSyncDisabled ? s.upworkMenuItemDisabled : {}) }}
            onClick={handleSyncProposals}
            disabled={proposalsSyncDisabled}
            title={proposalsSyncTitle}
          >
            {proposalsSyncLabel}
          </button>
          <button style={{ ...s.upworkMenuItem, color: '#c0392b' }} onClick={handleDisconnect}>Disconnect</button>
        </div>
      )}
    </div>
  );
}

function Nav({ page, setPage }) {
  const { user, logout, isSuperAdmin } = useAuth();
  return (
    <nav style={s.nav}>
      <div style={s.navBrand}>⚡ Leads Funnel Rev9</div>
      <div style={s.navLinks}>
        <button style={{ ...s.navLink, ...(page === 'pipeline' ? s.navLinkActive : {}) }} onClick={() => setPage('pipeline')}>Pipeline</button>
        <button style={{ ...s.navLink, ...(page === 'leads' ? s.navLinkActive : {}) }} onClick={() => setPage('leads')}>Leads</button>
        {isSuperAdmin() && (
          <button style={{ ...s.navLink, ...(page === 'automations' ? s.navLinkActive : {}) }} onClick={() => setPage('automations')}>Automations</button>
        )}
        {isSuperAdmin() && (
          <button style={{ ...s.navLink, ...(page === 'admin' ? s.navLinkActive : {}) }} onClick={() => setPage('admin')}>Team</button>
        )}
      </div>
      <div style={s.navUser}>
        {isSuperAdmin() && <UpworkButton />}
        <span style={s.userName}>{user?.name}</span>
        {isSuperAdmin() && <span style={s.adminBadge}>Admin</span>}
        <button style={s.logoutBtn} onClick={logout}>Sign out</button>
      </div>
    </nav>
  );
}

function AppInner() {
  const { user, loading } = useAuth();
  const [page, setPage] = React.useState('pipeline');

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#888', fontFamily: 'Inter, sans-serif' }}>Loading...</div>;
  if (!user) return <LoginPage />;

  // Builder takes over the full page — no nav bar.
  if (page === 'pipeline-builder') {
    return <PipelineBuilderPage onBack={() => setPage('pipeline')} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f4f0', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Nav page={page} setPage={setPage} />
      {page === 'pipeline' && <PipelinePage onOpenBuilder={() => setPage('pipeline-builder')} />}
      {page === 'leads' && <LeadsPage />}
      {page === 'automations' && <AutomationsPage />}
      {page === 'admin' && <AdminPage />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

const s = {
  nav: { background: '#fff', borderBottom: '1px solid #e0e0e0', padding: '0 20px', height: 52, display: 'flex', alignItems: 'center', gap: 20, position: 'sticky', top: 0, zIndex: 100 },
  navBrand: { fontSize: 15, fontWeight: 700, color: '#534AB7', letterSpacing: '-0.02em', marginRight: 8 },
  navLinks: { display: 'flex', gap: 4, flex: 1 },
  navLink: { padding: '6px 14px', borderRadius: 8, border: 'none', background: 'none', fontSize: 13, color: '#666', cursor: 'pointer', fontWeight: 500 },
  navLinkActive: { background: '#EEEDFE', color: '#534AB7' },
  navUser: { display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' },
  userName: { fontSize: 13, color: '#444', fontWeight: 500 },
  adminBadge: { fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#EEEDFE', color: '#534AB7', fontWeight: 600 },
  logoutBtn: { fontSize: 12, padding: '5px 12px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: '#666', cursor: 'pointer' },
  upworkBtn: { fontSize: 12, padding: '5px 12px', borderRadius: 8, border: '1px solid #14a800', background: '#fff', color: '#14a800', cursor: 'pointer', fontWeight: 500 },
  upworkConnectedBtn: { fontSize: 12, padding: '5px 12px', borderRadius: 8, border: '1px solid #14a800', background: '#f0faf0', color: '#14a800', cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 },
  upworkDot: { width: 7, height: 7, borderRadius: '50%', background: '#14a800', display: 'inline-block' },
  upworkMenu: { position: 'absolute', right: 0, top: '110%', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 200, minWidth: 175, overflow: 'hidden' },
  upworkMenuItem: { display: 'block', width: '100%', padding: '9px 14px', border: 'none', background: 'none', fontSize: 13, cursor: 'pointer', textAlign: 'left', color: '#333' },
  upworkMenuItemDisabled: { color: '#bbb', cursor: 'not-allowed' },
};
